import {
    Avatar,
    Field,
    InteractionTag,
    InteractionTagPrimary,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Overflow,
    OverflowItem,
    shorthands,
    Tag,
    tagClassNames,
    TagGroup,
    tokens,
    useIsOverflowItemVisible,
    useOverflowMenu
} from "@fluentui/react-components";
import type { IdentityDetail } from "../../../interfaces";

export interface IUserTagGroupProps {
    items?: IdentityDetail[];
    fieldLabel: string;
    isDismissible?: boolean;
    isLoading?: boolean;
    onRemoveItem?(id: string): void;
}

const useStyles = makeStyles({
    outerDiv: {
        paddingBottom: '20px',
        maxWidth: 'fit-content'
    },
    container: {
        marginTop: '10px',
        overflow: "hidden",
        padding: "5px",
        zIndex: 0,
        height: "fit-content",
        minWidth: "150px",
        width: "100%",
    },
    tagGroup: {
        display: "flex"
    }
});


type OverflowMenuItemProps = {
    tag: IdentityDetail;
};

const useMenuItemStyles = makeStyles({
    menuItem: {
        padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalXS}`,
        ":hover": {
            [`& .${tagClassNames.root}`]: {
                color: tokens.colorNeutralForeground2Hover,
            },
        },
    },
    tag: {
        backgroundColor: "transparent",
        ...shorthands.borderColor("transparent"),
    },
});


const OverflowMenuItem = (props: OverflowMenuItemProps) => {
    const { tag } = props;
    const isVisible = useIsOverflowItemVisible(tag.id);
    const styles = useMenuItemStyles();

    if (isVisible || !tag.id) {
        return null;
    }

    return (
        <MenuItem key={tag.id} className={styles.menuItem}>
            <Tag shape="circular" value={tag.id} media={<Avatar name={tag.displayName} />}>
                {tag.displayName}
            </Tag>
        </MenuItem>
    );
};

interface IOverflowMenuProps {
    items: IdentityDetail[];
}

const OverflowMenu = (props: IOverflowMenuProps) => {
    const { items } = props;
    const { ref, isOverflowing, overflowCount } = useOverflowMenu<HTMLButtonElement>();

    if (!isOverflowing) {
        return null;
    }

    return (
        <InteractionTag>
            <Menu>
                <MenuTrigger disableButtonEnhancement>
                    <InteractionTagPrimary
                        ref={ref}
                        aria-label={`${overflowCount} more tags`}>
                        {`+${overflowCount}`}
                    </InteractionTagPrimary>
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        {items.map((item) => (
                            <OverflowMenuItem key={item.id} tag={item} />
                        ))}
                    </MenuList>
                </MenuPopover>
            </Menu>
        </InteractionTag>
    );
};

export const UserTagGroup = (props: IUserTagGroupProps) => {
    const { fieldLabel, items, isLoading, isDismissible, onRemoveItem } = props;
    const styles = useStyles();
    if (isDismissible) {
        return (
            <>
                {items && items?.length > 0 &&
                    <div className={styles.outerDiv}>
                        <Field label={fieldLabel} size="large">
                            <div className={styles.container}>
                                <Overflow minimumVisible={3} padding={60}>
                                    <TagGroup className={styles.tagGroup} onDismiss={(_, data) => onRemoveItem?.(data.value)} >
                                        {items.map((item) => (
                                            <OverflowItem key={`overflowItem1_${item.id}`} id={item.id}>
                                                <Tag shape="circular"
                                                    value={item.id}
                                                    media={<Avatar name={item.displayName} />}
                                                    dismissible
                                                    dismissIcon={{ "aria-label": "remove" }}
                                                    disabled={isLoading} >
                                                    {item.displayName}
                                                </Tag>
                                            </OverflowItem>

                                        ))}
                                        <OverflowMenu items={items} />
                                    </TagGroup>

                                </Overflow>
                            </div>
                        </Field>
                    </div>}
            </>
        );

    }

    return (
        <>
            {items && items?.length > 0 &&
                <div className={styles.outerDiv}>
                    <Field label={fieldLabel} size="large">
                        <div className={styles.container}>
                            <Overflow minimumVisible={3} padding={60}>
                                <TagGroup className={styles.tagGroup} >
                                    {items.map((item) => (
                                        <OverflowItem key={`overflowItem2_${item.id}`} id={item.id}>
                                            <Tag shape="circular" value={item.id} media={<Avatar name={item.displayName} />}>
                                                {item.displayName}
                                            </Tag>
                                        </OverflowItem>
                                    ))}
                                    <OverflowMenu items={items} />
                                </TagGroup>
                            </Overflow>
                        </div>
                    </Field>
                </div>}
        </>
    );
};
